import { hyphenate } from '@landsbankinn/utils';
import { Element } from '@prismicio/react';
import type { RTAnyNode, RTLabelNode } from '@prismicio/types';
import React from 'react';
import { Embed } from './Embed';
import { InlineExpression } from './InlineExpression';
import { PrismicLink } from './Prismic';

const createHtmlSerializer = (parse: any) =>
  function serializer(
    type: string,
    element: RTAnyNode | RTLabelNode,
    content: string | undefined,
    children: React.ReactNode,
    key: string,
  ) {
    switch (type) {
      case Element.heading1:
        return <h1 key={key}>{children}</h1>;

      case Element.heading2:
        return <h2 key={key}>{children}</h2>;

      case Element.heading3:
        return <h3 key={key}>{children}</h3>;

      case Element.heading4:
        return <h4 key={key}>{children}</h4>;

      case Element.heading5:
        return <h5 key={key}>{children}</h5>;

      case Element.heading6:
        return <small key={key}>{children}</small>;

      case Element.paragraph:
        return <p key={key}>{children}</p>;

      case Element.preformatted:
        return <pre key={key}>{children}</pre>;

      case Element.strong:
        return <strong key={key}>{children}</strong>;

      case Element.em:
        return <em key={key}>{children}</em>;

      case Element.listItem:
        return <li key={key}>{children}</li>;

      case Element.oListItem:
        return <li key={key}>{children}</li>;

      case Element.list:
        return <ul key={key}>{children}</ul>;

      case Element.oList:
        return <ol key={key}>{children}</ol>;

      case Element.image:
        if (element.type === 'image') {
          if (element.linkTo) {
            return (
              <PrismicLink
                key={key}
                value={element.linkTo}
                title={element.alt ?? undefined}
              >
                <a>
                  <img src={element.url} alt={element.alt ?? undefined} />
                </a>
              </PrismicLink>
            );
          }
          return (
            <img key={key} src={element.url} alt={element.alt ?? undefined} />
          );
        }
        break;
      case Element.embed:
        if (element.type === 'embed' && element.oembed.html) {
          return (
            <Embed
              key={key}
              html={element.oembed.html}
              type="inline"
              noSpace={false}
            />
          );
        }
        break;
      case Element.hyperlink:
        if (element.type === 'hyperlink') {
          if (
            String(element.data?.url).match(/e.infogr/) ||
            String(element.data?.url).match(/infogram.com/)
          ) {
            return (
              <Embed
                html={`<iframe src="${element.data.url}" width="100%" height="360" scrolling="no" frameborder="0" allowtransparency="true" allowfullscreen></iframe>`}
                key={key}
                type="inline"
                ar={3 / 4}
              />
            );
          }

          return (
            <PrismicLink key={key} value={element.data}>
              <a>{children}</a>
            </PrismicLink>
          );
        }
        break;

      case Element.label: {
        if ((element as RTLabelNode)?.data?.label === 'superscript') {
          return <sup key={key}>{children}</sup>;
        } else if ((element as RTLabelNode)?.data?.label === 'subscript') {
          return <sub key={key}>{children}</sub>;
        }
        return <span key={key}>{children}</span>;
      }

      case Element.span:
        if (content) {
          return content.split('\n').reduce((acc: any, p: any) => {
            const parsed = parse(p);
            if (acc.length === 0) {
              return [].concat(parsed);
            } else {
              const brIndex = (acc.length + 1) / 2 - 1;
              return acc.concat(<br key={brIndex} />).concat(parsed);
            }
          }, []);
        } else {
          return null;
        }
      default:
        return null;
    }
  };

export const parseTariff = (str?: string | null) => {
  return str?.split(/(%[a-z]+\d+%)/).map((item, i) => {
    const tariff = item.match(/%(fee|interest)(\d+)%/);
    if (tariff) {
      return (
        <InlineExpression key={i} type={tariff[1] as any} index={tariff[2]} />
      );
    }
    return item;
  });
};

export const htmlSerializer = createHtmlSerializer(parseTariff);

export const htmlSerializerHyphenated = createHtmlSerializer((str: string) => {
  return parseTariff(str)?.map((x) => {
    if (typeof x === 'string') {
      return hyphenate(x);
    }
    return x;
  });
});
